import type { CustomerMatchReason, CustomerSearchRow } from '../../services/crmFindCustomersService';
import {
    customerMatchLabel,
    customerRowEmail,
    customerRowName,
    customerRowNumber,
    customerRowPhone,
} from '../CRM/Directory/customerRowFormat';

/**
 * Header search (Cmd/Ctrl+K) customer results. The rows come from the same `crm_find_customers` search as the
 * Customers page (customer number, booking code / PNR, phone in any format, e-mail, name), which runs as the signed-in
 * staff member and only returns customers `can_access_customer()` allows. Labels use the Customers page formatters,
 * so a result reads the same in both places and is masked the same way in privacy mode.
 */

/** Matches on an identifier rather than a name: shown above page and other record matches. */
const EXACT_REASONS = new Set<CustomerMatchReason>(['customer_number', 'booking_code', 'phone', 'email']);

export function isExactCustomerMatch(row: Pick<CustomerSearchRow, 'matchReason'>): boolean {
    return row.matchReason !== null && EXACT_REASONS.has(row.matchReason);
}

export interface CustomerSearchDisplay {
    title: string;
    subtitle: string;
}

export function customerSearchDisplay(row: CustomerSearchRow, privacy: boolean): CustomerSearchDisplay {
    const name = customerRowName(row, privacy);
    if (row.entityType === 'company') {
        return { title: privacy ? 'Company hidden' : name, subtitle: 'Company' };
    }
    const number = customerRowNumber(row, privacy);
    const contact = customerRowEmail(row.email, privacy) ?? customerRowPhone(row.phone, privacy);
    const subtitle = [customerMatchLabel(row, privacy), contact].filter(Boolean).join(' · ');
    return {
        title: number ? `${number} · ${name}` : name,
        subtitle: subtitle || 'Customer',
    };
}
